/**
 * The requests of a sheet promotion (ephemeral → server): the create — or the
 * recovery of a sheet an earlier attempt or a duplicate id already stored —
 * and the reconciliation of every nested row with what the server holds.
 * Every write is checked (`checkedPromoteWrite`): a refused one throws a
 * `PromoteWriteError` and the handle stays un-promoted. Holds no state of
 * its own: the client hands it its API and its sheets client.
 */
import type { Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import type { TypedApi } from "@tento-core/typed-api";
import { Tracing } from "@tento-core/tracing";
import { RowId, RowNetworkKey, SheetId, SheetNetworkKey } from "@tento-lona/sheets";
import { Network } from "../../network.ts";
import { PromoteWriteError, SdkRequestError } from "../../errors.ts";
import { SheetsClient, sheetListCache } from "../../services/sheets/sheets-client.ts";
import {
  checkedPromoteWrite,
  createVerdictOf,
  CROSS_OWNER_SHEET_READ_STATUSES,
  type ExistingPromotedSheet,
  isFreshCreateResponse,
  type PromotedChildren,
  type PromotedSheet,
  type PromoteServerTree,
  type PromotionNewUserSheet,
  promotionNewRow,
  promotionRowBody,
  promotionRowKey,
  promotionRowLookupKey,
  samePromotedPosition,
} from "./promotion-rows.ts";

export class PromotionWrites {
  readonly #api: TypedApi<Network.Routes>;
  readonly #sheets: () => SheetsClient;

  constructor(api: TypedApi<Network.Routes>, sheets: () => SheetsClient) {
    this.#api = api;
    this.#sheets = sheets;
  }

  async createOrLoad(
    newSheet: PromotionNewUserSheet,
    sheetId: string,
  ): Promise<PromotedSheet> {
    const parsed = SheetId.of(sheetId);
    const canonicalId = parsed.toString();
    Tracing.log("cow-promote-create-start", {
      sheetId: canonicalId,
      requestId: String(newSheet.id),
      lookupKey: newSheet.lookupKey ?? null,
      rowsSent: newSheet.rows?.length ?? 0,
    });
    try {
      // Tag with PROMOTE_ACTOR so the app's sheet-invalidation dispatch skips
      // the whole-sheet cell rebind for this transparent re-key (which would
      // otherwise blur the focused editor). Sidebar list still refreshes.
      const sheet = await this.#sheets().create(
        newSheet,
        SheetsClient.PROMOTE_ACTOR,
      );
      // HTTP 200 is a fresh create OR the owner's existing sheet handed back
      // whole (`create_with_rows_idempotent`); only the rows tell them apart.
      const fresh = isFreshCreateResponse(newSheet.rows ?? [], sheet.rows ?? []);
      if (!fresh) {
        Tracing.log("cow-promote-create-reused", {
          sheetId: canonicalId,
          rowsSent: newSheet.rows?.length ?? 0,
          serverRows: sheet.rows?.length ?? 0,
        });
      }
      return { sheet, adopted: !fresh };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const duplicateSheet = message.includes("spreadsheets_pkey") ||
        message.includes("UniqueViolation");
      if (!duplicateSheet) {
        Tracing.log("cow-promote-create-error", {
          sheetId: canonicalId,
          message,
        });
        // `sheets.create` already unwrapped the result into `reason:
        // details`; keep that text and name the step.
        const verdict = createVerdictOf(
          err instanceof SdkRequestError ? err : null,
        );
        throw new PromoteWriteError({
          step: "create",
          method: "POST",
          path: "/api/sheets",
          failure: verdict,
          reason: message,
        });
      }

      Tracing.warn("net")(
        `POST /api/sheets duplicate for ${canonicalId}; loading existing sheet`,
      );
      Tracing.log("cow-promote-duplicate-existing", {
        sheetId: canonicalId,
        message,
      });
      const loaded = await this.loadExisting(parsed);
      const existing = loaded.sheet;
      if (existing == null) {
        // A duplicate we can't READ is not a duplicate we can ADOPT. This
        // recovery used to assume every duplicate meant "the sheet is
        // already ours", so a cross-owner id fell out as a bare `sheet not
        // found` inside the add-row path and the user's row silently failed
        // to save. Split the two cases: a rejection is terminal for THIS id
        // (no retry can grant access); both are refused requests, thrown as
        // the promote's own error for the caller to report (the SDK shows
        // no UI).
        if (loaded.crossOwner) {
          Tracing.log("cow-promote-duplicate-cross-owner", {
            sheetId: canonicalId,
            status: loaded.status,
          });
          throw new PromoteWriteError({
            step: "get",
            method: "GET",
            path: `/api/sheets/${loaded.sheetKey}`,
            // A cross-owner answer is a 401/403 refusal.
            failure: SdkRequestError.answered(loaded.status),
            reason: "cross_owner",
            details: `Sheet ${canonicalId} is already saved under a different ` +
              `account (the server answered ${loaded.status} when this ` +
              `session tried to read it), so it can't be claimed here. ` +
              `Open the sheet without the fixed \`id\` so a new one is ` +
              `minted, or ask the owner to share it.`,
          });
        }
        Tracing.log("cow-promote-duplicate-missing", {
          sheetId: canonicalId,
          status: loaded.status,
        });
        // No error code on the read is a 200 with an empty body — an
        // answered miss, as `SheetNotFoundError` reports it.
        const missVerdict = SdkRequestError.answered(loaded.status ?? 200);
        throw new PromoteWriteError({
          step: "get",
          method: "GET",
          path: `/api/sheets/${loaded.sheetKey}`,
          failure: missVerdict,
          reason: "not_found",
          details: `sheet not found: ${canonicalId}`,
        });
      }
      const existingRows = existing.rows ?? [];
      Tracing.log("cow-promote-loaded-existing", {
        sheetId: canonicalId,
        serverId: SheetId.of(String(existing.id)).toString(),
        serverRows: existingRows.length,
      });
      sheetListCache(this.#sheets()).insert(existing);
      return { sheet: existing, adopted: true };
    }
  }

  /**
   * Everything a COW promote writes after `POST /api/sheets`, as a RECONCILE
   * against `server` — what the server already holds: the create's own roots
   * on a fresh promote, or the whole stored sheet when the create handed back
   * an existing one, a retry resumes, or a duplicate create adopts. So a
   * retry writes exactly what is missing, and a retry with nothing missing
   * writes nothing. `serverIdOf` (local key → server key) arrives with every
   * correlation made so far and is extended in place with every row written
   * or found, BEFORE any corrective PATCH, so a failure part-way leaves the
   * next attempt knowing what is stored.
   *
   * 1. ROOTS (`roots`, the rows the create carried, with what was sent). One
   *    with no server row — `create_with_rows` saves the sheet then adds the
   *    roots one at a time outside a transaction, so an interrupted create
   *    stores only some — is added with the single-row route
   *    `POST /api/sheets/{sheet}/row` (`api/sheets/__sheet_key/row/post.rs`),
   *    which keeps type / label / attributes / formula / position / timeScale
   *    and reuses a lookup-keyed row via `fork_if_neccesary`; it has no
   *    `heightPx`, so a non-lookup row's is PATCHed. An answer that is not
   *    this row (another type, or a reused row another local row already
   *    holds) is not recorded — the row stays missing. A root that IS
   *    mapped (the create's, a correlated one, or one an earlier attempt
   *    added) skips only the creation: its `timeScale` / `heightPx`
   *    correction still runs against the stored row, because the add is
   *    recorded before its PATCH, and a PATCH that failed or lost its answer
   *    must not be skipped on the retry. A lookup-keyed root is the user's
   *    shared row and is never PATCHed.
   * 2. NESTED rows, parents before children (`rows` is the serialized tree in
   *    pre-order). `create_with_rows` is flat, so each is written under its
   *    parent's SERVER id:
   *    - found already stored — by the id an earlier attempt recorded,
   *      else by type + in-parent position among the parent's unclaimed
   *      stored children, else as an unclaimed stored ROOT with its type,
   *      label and position (a row an earlier attempt added for this parent
   *      and never re-parented) — it is not written again. A retained id
   *      never implies its re-parent landed: the row's ACTUAL server parent
   *      and position are compared with the local ones and a `reorder` is
   *      sent when they differ; then its missing `timeScale` / `heightPx`
   *      correction;
   *    - otherwise, without a lookup key, it goes through
   *      `POST .../row/{parent}/hydrate-children` tagged with its local key
   *      as `clientId`. The answer is checked: a fresh hydrate answers one NEW
   *      row per sent child with the sent type. Anything else is the
   *      `children_hydrated` fast path — the parent was hydrated before (and
   *      a child since deleted by another session), so the server created
   *      NOTHING and zipped our client ids onto its existing children — and
   *      each child is instead added (`POST /api/sheets/{sheet}/row`) and
   *      re-parented (`reorder`);
   *    - `hydrate-children` stores neither `timeScale` nor `heightPx` (it
   *      stamps the sheet default), so a differing one is PATCHed;
   *    - WITH a lookup key (`:~lona:weather`) it rode the create (hydrate
   *      drops `lookupKey`, which would mint a fresh row instead of reusing
   *      the user's canonical one) and is re-parented with `reorder`, unless
   *      it already sits under the parent at its position.
   *
   * A nested row whose parent has no server row is counted `unresolved` and
   * left unwritten; the caller refuses to mark the handle synced while any
   * local row lacks a server row (`PromoteIncompleteError`). The server
   * tree is tracked as it is written — each stored row's parent and
   * position — and the result's `misplaced` lists every mapped row whose
   * stored parent, position or corrected metadata still differs from the
   * local row, so the caller verifies tree EDGES, not only ids. Calls the API
   * directly, never `RowAccessor.add` / `.hydrateChildren` / `.move`: those
   * await `ensurePromoted`, which is THIS promote — they would deadlock on
   * it. Every write goes through `checkedPromoteWrite`: a rejected one
   * throws a `PromoteWriteError` and the handle stays un-promoted.
   */
  async reconcileRows(
    sheetKey: string,
    rows: readonly Network.SheetRow[],
    parentOf: ReadonlyMap<string, string>,
    roots: readonly {
      readonly row: Network.SheetRow;
      readonly sent: Network.NewRowWithAttributes;
    }[],
    serverIdOf: Map<string, string>,
    server: PromoteServerTree,
  ): Promise<PromotedChildren> {
    const api = this.#api;
    const childrenOf = new Map<string, Network.SheetRow[]>();
    for (const row of rows) {
      const parent = parentOf.get(promotionRowKey(row.id));
      if (parent == null) continue;
      const siblings = childrenOf.get(parent) ?? [];
      siblings.push(row);
      childrenOf.set(parent, siblings);
    }
    const persisted = new Map<string, Network.SheetRow>(server.rows);
    // Each stored row's server parent NOW (`null`: a root), kept current as
    // this reconcile adds, hydrates and re-parents.
    const serverParentOf = new Map<string, Option<string>>();
    for (const key of server.rows.keys()) serverParentOf.set(key, null);
    for (const [parentKey, kids] of server.childrenOf) {
      for (const kid of kids) serverParentOf.set(kid, parentKey);
    }
    const claimed = new Set<string>(serverIdOf.values());
    const serverChildren = new Map<string, string[]>();
    let parents = 0;
    let hydrated = 0;
    let reparented = 0;
    let patched = 0;
    let alreadyPersisted = 0;
    let unresolved = 0;
    let rowsAdded = 0;
    let readded = 0;
    // Whether the stored row carries the local row's correctable metadata:
    // a `timeScale` the local row names, and its `heightPx`.
    const metadataMatches = (
      local: Network.SheetRow,
      stored: Network.SheetRow,
    ): boolean =>
      (local.timeScale == null || stored.timeScale === local.timeScale) &&
      (local.heightPx ?? null) === (stored.heightPx ?? null);
    // Whether the stored row sits under `parentKey` at `position` right now.
    const inPlace = (
      serverKey: string,
      parentKey: Option<string>,
      position: Network.SheetRow["position"],
    ): boolean =>
      (serverParentOf.get(serverKey) ?? null) === parentKey &&
      samePromotedPosition(persisted.get(serverKey)?.position ?? null, position);
    // `hydrate-children` stores neither `timeScale` nor `heightPx` (it stamps
    // the sheet default) and the single-row add has no `heightPx`, so a row
    // whose stored value differs from the local one is corrected — whether it
    // was written just now or by an attempt that failed before its PATCH.
    // A local row with no `timeScale` keeps whatever the server stamped.
    const correct = async (
      kid: Network.SheetRow,
      serverKey: string,
      stored: Network.SheetRow,
    ): Promise<void> => {
      const patch: Network.PatchRow_PendingValidation = {};
      let corrected = stored;
      if (kid.timeScale != null && stored.timeScale !== kid.timeScale) {
        patch.timeScale = kid.timeScale;
        corrected = { ...corrected, timeScale: kid.timeScale };
      }
      if ((kid.heightPx ?? null) !== (stored.heightPx ?? null)) {
        patch.heightPx = kid.heightPx ?? null;
        corrected = { ...corrected, heightPx: kid.heightPx ?? null };
      }
      if (corrected !== stored) {
        await checkedPromoteWrite(
          "update",
          "PATCH",
          `/api/rows/${serverKey}`,
          () =>
            api.patchResult("/api/rows/{rowKey}", {
              path: { rowKey: RowNetworkKey.fromWire(serverKey) },
              body: patch,
            }),
        );
        patched++;
      }
      persisted.set(serverKey, corrected);
    };
    // Re-parent a stored row under `parentKey` at `position` (`reorder`).
    const reparent = async (
      serverKey: string,
      parentKey: string,
      position: Network.SheetRow["position"],
    ): Promise<void> => {
      await checkedPromoteWrite(
        "reorder",
        "POST",
        `/api/sheets/${sheetKey}/row/${serverKey}/reorder`,
        () =>
          api.postResult("/api/sheets/{sheetKey}/row/{rowKey}/reorder", {
            path: { sheetKey: SheetNetworkKey.fromWire(sheetKey), rowKey: RowNetworkKey.fromWire(serverKey) },
            body: Network.RowReorderBodySchema.make({ parentRowKey: RowNetworkKey.fromWire(parentKey), position, recanonicalizationThreshold: null }),
          }),
      );
      serverParentOf.set(serverKey, parentKey);
      const moved = persisted.get(serverKey);
      if (moved != null) persisted.set(serverKey, { ...moved, position });
      reparented++;
    };
    // Add ONE top-level row (`POST /api/sheets/{sheet}/row`) and record it
    // for `localKey` — unless the answer is not this row: a different type,
    // or a lookup-keyed row the server reused that another local row
    // already holds. `null`: nothing recorded, the row stays missing.
    const addRow = async (
      localKey: string,
      local: Network.SheetRow,
      sent: Network.NewRowWithAttributes,
    ): Promise<Option<Network.SheetRow>> => {
      const added: Network.SheetRow = await checkedPromoteWrite(
        "add",
        "POST",
        `/api/sheets/${sheetKey}/row`,
        () =>
          api.postResult("/api/sheets/{sheetKey}/row", {
            path: { sheetKey: SheetNetworkKey.fromWire(sheetKey) },
            body: promotionRowBody(sent),
          }),
      );
      const serverKey = promotionRowKey(added.id);
      if (
        claimed.has(serverKey) ||
        (promotionRowLookupKey(local.lookupKey) == null && added.type !== local.type)
      ) {
        Tracing.log("cow-promote-add-mismatch", {
          sheetId: sheetKey,
          localKey,
          serverKey,
          claimed: claimed.has(serverKey),
          type: added.type,
        });
        return null;
      }
      serverIdOf.set(localKey, serverKey);
      claimed.add(serverKey);
      persisted.set(serverKey, added);
      // A reused lookup-keyed row keeps wherever it is stored.
      if (!serverParentOf.has(serverKey)) serverParentOf.set(serverKey, null);
      rowsAdded++;
      return added;
    };

    for (const { row, sent } of roots) {
      const localKey = promotionRowKey(row.id);
      let serverKey = serverIdOf.get(localKey) ?? null;
      let stored = serverKey != null ? persisted.get(serverKey) ?? null : null;
      if (serverKey != null && stored == null) {
        // Recorded, but not a stored row: add it like any missing root.
        serverIdOf.delete(localKey);
        claimed.delete(serverKey);
        serverKey = null;
      }
      if (serverKey == null || stored == null) {
        const added = await addRow(localKey, row, sent);
        if (added == null) continue;
        serverKey = promotionRowKey(added.id);
        stored = added;
      }
      // Mapped or just added, the stored root is corrected: only its
      // CREATION is skipped when an earlier attempt recorded it (the add is
      // recorded before its PATCH). A lookup-keyed row is the user's shared
      // row: never PATCHed here.
      // An explicit data key creates an anonymous shim. A retained mapping to
      // a historical keyed row remains intact, with no metadata patch to it.
      if (promotionRowLookupKey(row.lookupKey) == null && stored.lookupKey == null) {
        await correct(row, serverKey, stored);
      }
    }

    for (const parent of rows) {
      const localParent = promotionRowKey(parent.id);
      const kids = childrenOf.get(localParent) ?? [];
      if (kids.length === 0) continue;
      const serverParent = serverIdOf.get(localParent) ?? null;
      if (serverParent == null) {
        unresolved += kids.length;
        continue;
      }
      parents++;
      const storedKids = server.childrenOf.get(serverParent) ?? [];

      const toHydrate: Network.SheetRow[] = [];
      const present: { kid: Network.SheetRow; serverKey: string }[] = [];
      for (const kid of kids) {
        if (kid.lookupKey != null) continue;
        const localKey = promotionRowKey(kid.id);
        let serverKey = serverIdOf.get(localKey) ?? null;
        if (serverKey != null && !persisted.has(serverKey)) {
          // Recorded by an earlier attempt but gone from the server now.
          serverIdOf.delete(localKey);
          claimed.delete(serverKey);
          serverKey = null;
        }
        if (serverKey == null) {
          serverKey = storedKids.find((id) => {
            const stored = server.rows.get(id);
            return stored != null && !claimed.has(id) &&
              stored.type === kid.type &&
              samePromotedPosition(stored.position, kid.position);
          }) ?? null;
          if (serverKey != null) {
            serverIdOf.set(localKey, serverKey);
            claimed.add(serverKey);
          }
        }
        if (serverKey == null) {
          // An earlier attempt (another session's, or this handle's before
          // a restart) re-created this row as a ROOT under an
          // already-hydrated parent and never re-parented it: adopt that
          // row, not a second copy. Matched strictly — an unclaimed,
          // non-lookup root with the kid's type, label and position.
          for (const [key, stored] of persisted) {
            if (
              !claimed.has(key) &&
              (serverParentOf.get(key) ?? null) == null &&
              stored.lookupKey == null &&
              stored.type === kid.type &&
              (stored.label ?? null) === (kid.label ?? null) &&
              samePromotedPosition(stored.position, kid.position)
            ) {
              serverKey = key;
              break;
            }
          }
          if (serverKey != null) {
            Tracing.log("cow-promote-orphan-adopted", {
              sheetId: sheetKey,
              localKey,
              serverKey,
              parent: serverParent,
            });
            serverIdOf.set(localKey, serverKey);
            claimed.add(serverKey);
          }
        }
        if (serverKey == null) toHydrate.push(kid);
        else present.push({ kid, serverKey });
      }

      for (const { kid, serverKey } of present) {
        // A retained id never implies its re-parent landed (an add whose
        // `reorder` was rejected keeps its id as a ROOT): compare the row's
        // actual server parent + position and move it when they differ.
        if (inPlace(serverKey, serverParent, kid.position)) {
          alreadyPersisted++;
        } else {
          await reparent(serverKey, serverParent, kid.position);
        }
        await correct(
          kid,
          serverKey,
          nonnull(persisted.get(serverKey), "promote: matched stored row"),
        );
      }

      if (toHydrate.length > 0) {
        const response = await checkedPromoteWrite(
          "hydrate-children",
          "POST",
          `/api/sheets/${sheetKey}/row/${serverParent}/hydrate-children`,
          () =>
            api.postResult(
              "/api/sheets/{sheetKey}/row/{rowKey}/hydrate-children",
              {
                path: { sheetKey: SheetNetworkKey.fromWire(sheetKey), rowKey: RowNetworkKey.fromWire(serverParent) },
                body: {
                  children: toHydrate.map((kid) => ({
                    ...promotionNewRow(kid, kid.position),
                    clientId: promotionRowKey(kid.id),
                  })),
                },
              },
            ),
        );
        // A fresh hydrate answers exactly one NEW row per sent child, with
        // its client id and type. Anything else is the `children_hydrated`
        // fast path, which created nothing.
        const hydratedRows = response?.hydratedRows ?? [];
        const sentByKey = new Map<string, Network.SheetRow>();
        for (const kid of toHydrate) sentByKey.set(promotionRowKey(kid.id), kid);
        const created = new Map<string, Network.SheetRow>();
        let fresh = hydratedRows.length === toHydrate.length;
        for (const entry of hydratedRows) {
          if (!fresh) break;
          const clientId = entry.clientId ?? null;
          const kid = clientId != null ? sentByKey.get(clientId) ?? null : null;
          const serverKey = promotionRowKey(entry.row.id);
          if (
            clientId == null || kid == null || created.has(clientId) ||
            entry.row.type !== kid.type || persisted.has(serverKey) ||
            claimed.has(serverKey)
          ) {
            fresh = false;
            break;
          }
          created.set(clientId, entry.row);
        }
        if (fresh) {
          // Record every correlation BEFORE any corrective PATCH: if one of
          // those fails, the retry knows these rows are already stored.
          for (const kid of toHydrate) {
            const localKey = promotionRowKey(kid.id);
            const row = nonnull(created.get(localKey), "promote: checked hydrate row");
            const serverKey = promotionRowKey(row.id);
            serverIdOf.set(localKey, serverKey);
            claimed.add(serverKey);
            persisted.set(serverKey, row);
            serverParentOf.set(serverKey, serverParent);
            hydrated++;
          }
          for (const kid of toHydrate) {
            const row = nonnull(created.get(promotionRowKey(kid.id)), "promote: checked hydrate row");
            await correct(kid, promotionRowKey(row.id), row);
          }
        } else {
          Tracing.log("cow-promote-hydrate-closed", {
            sheetId: sheetKey,
            parent: serverParent,
            sent: toHydrate.length,
            answered: hydratedRows.length,
          });
          for (const kid of toHydrate) {
            const localKey = promotionRowKey(kid.id);
            const added = await addRow(
              localKey,
              kid,
              promotionNewRow(kid, kid.position),
            );
            if (added == null) continue;
            const serverKey = promotionRowKey(added.id);
            readded++;
            // Recorded as a ROOT until this lands; a retry that finds it
            // there re-parents it (the `present` check above).
            await reparent(serverKey, serverParent, kid.position);
            await correct(
              kid,
              serverKey,
              nonnull(persisted.get(serverKey), "promote: re-added row"),
            );
          }
        }
      }

      for (const kid of kids) {
        if (kid.lookupKey == null) continue;
        const serverKey = serverIdOf.get(promotionRowKey(kid.id)) ?? null;
        if (serverKey == null) {
          unresolved++;
          continue;
        }
        if (inPlace(serverKey, serverParent, kid.position)) {
          alreadyPersisted++;
        } else {
          await reparent(serverKey, serverParent, kid.position);
        }
        const stored = nonnull(persisted.get(serverKey), "promote: keyed child");
        if (promotionRowLookupKey(kid.lookupKey) == null && stored.lookupKey == null) {
          await correct(kid, serverKey, stored);
        }
      }

      const serverKids: string[] = [];
      for (const kid of kids) {
        const serverKey = serverIdOf.get(promotionRowKey(kid.id)) ?? null;
        if (serverKey != null) serverKids.push(serverKey);
      }
      serverChildren.set(serverParent, serverKids);
    }

    // The tree-edge + metadata half of the completeness check, over the
    // server tree as written: every mapped row sits under its local
    // parent's server row (a root under none) at its position, and carries
    // the metadata `correct` guarantees. A shared lookup-keyed ROOT is
    // exempt (never moved or patched here); a nested lookup-keyed row's
    // edge is checked, its metadata is not.
    const sentPositionOf = new Map<string, Network.SheetRow["position"]>();
    for (const { row, sent } of roots) {
      if (sent.position != null) {
        sentPositionOf.set(promotionRowKey(row.id), sent.position);
      }
    }
    const misplaced: string[] = [];
    for (const row of rows) {
      const localKey = promotionRowKey(row.id);
      const serverKey = serverIdOf.get(localKey) ?? null;
      if (serverKey == null) continue; // missing: the caller counts it
      const stored = persisted.get(serverKey) ?? null;
      const localParent = parentOf.get(localKey) ?? null;
      let ok: boolean;
      if (stored == null) {
        ok = false;
      } else if (localParent == null) {
        ok = promotionRowLookupKey(row.lookupKey) != null || stored.lookupKey != null ||
          ((serverParentOf.get(serverKey) ?? null) == null &&
            samePromotedPosition(
              stored.position,
              sentPositionOf.get(localKey) ?? row.position,
            ) &&
            metadataMatches(row, stored));
      } else {
        const parentKey = serverIdOf.get(localParent) ?? null;
        ok = parentKey != null && inPlace(serverKey, parentKey, row.position) &&
          (promotionRowLookupKey(row.lookupKey) != null || stored.lookupKey != null || metadataMatches(row, stored));
      }
      if (!ok) misplaced.push(localKey);
    }

    const out: Network.SheetRow[] = [];
    for (const [serverKey, row] of persisted) {
      const children = serverChildren.get(serverKey);
      out.push(
        children == null
          ? row
          : { ...row, children: children.map((id) => RowId.of(id)) },
      );
    }
    return {
      rows: out,
      parents,
      hydrated,
      reparented,
      patched,
      alreadyPersisted,
      unresolved,
      rowsAdded,
      readded,
      misplaced,
    };
  }

  /**
   * Read an already-persisted sheet id after a duplicate-key create,
   * KEEPING the HTTP status. `sheets.get` collapses an auth rejection and a
   * genuine miss into the same `sheet not found` throw — which is exactly
   * how a squatted id read as a recoverable duplicate — so this goes to the
   * raw `getResult` (as `open(key)` does, through `readSheetForOpen`) and
   * classifies.
   *
   * Tries the canonical `ss_<hex>` key, then the bare uuid: the route accepts
   * both surface forms and the pre-existing recovery leaned on that fallback.
   * A rejection on EITHER attempt wins over a miss on the other — the
   * rejection is the load-bearing answer, and only a readable body outranks
   * it.
   */
  async loadExisting(
    sheetId: SheetId,
  ): Promise<ExistingPromotedSheet> {
    const canonical = sheetId.toString();
    const uuid = sheetId.toUuid();
    const keys = uuid == null || uuid === canonical
      ? [canonical]
      : [canonical, uuid];
    let status: Option<number> = null;
    let statusKey = canonical;
    let crossOwner = false;
    for (const sheetKey of keys) {
      const result = await this.#api.getResult("/api/sheets/{sheetKey}", {
        path: { sheetKey: SheetNetworkKey.fromWire(sheetKey) },
      });
      const sheet = result.ok();
      if (sheet != null) return { sheet, crossOwner: false, status: 200, sheetKey };
      const code = result.err()?.code ?? null;
      if (code == null) continue;
      // The first rejection is the answer: a later miss or rejection on the
      // other key form does not replace its status or its path.
      if (crossOwner) continue;
      const rejected = CROSS_OWNER_SHEET_READ_STATUSES.includes(code);
      status = code;
      statusKey = sheetKey;
      if (rejected) crossOwner = true;
    }
    return { sheet: null, crossOwner, status, sheetKey: statusKey };
  }
}
